import datetimeDecorator from "src/decorators/datetime.decorator";
import {
  ShipmentOrderBillingAmountEntity,
  ShipmentOrderLegRunPaymentAmountEntity,
  ShipmentOrderResponseEntity,
} from "src/entities/ShipmentOrderResponse.entity";

export interface ShipmentLegRunItem {
  id: number | string;
  shipmentId: number | string;
  sectionType: string;
  deliveryMethod: string;
  shipperName: string;
  route: string;
  loadingAddress: string;
  unloadingAddress: string;
  loadingOn: string;
  unloadingOn: string;
  loadingTimeWindow: string;
  unloadingTimeWindow: string;
  loadingStayingMinutes: number;
  unloadingStayingMinutes: number;
  quantity: number;
  weightKg: number | "";
  volumeM3: number | "";
  paymentAmountTotal: number;
  billingAmountTotal: number;
  dispatchStatus: string;
  carrierCompany: string | null;
  useLtl?: boolean;
  operatingCarrier: string | null;
  truckNumber: string | null;
  driverName: string | null;
  hierarchyLevel: number | string | null;
  planningLicenseName: string | null;
  paymentAmount: ShipmentOrderLegRunPaymentAmountEntity | null;
  billingAmount: ShipmentOrderBillingAmountEntity | null;
}

export const getEarliestDateFormatted = (
  times: { startAt: string; endAt: string }[]
): string => {
  if (!times?.length) return "";

  const sortedTimes = [...times].sort(
    (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime()
  );

  return datetimeDecorator.toYyyyMmDd(new Date(sortedTimes[0].startAt));
};

const formatTimeWindow = (times: { startAt: string; endAt: string }[]): string => {
  if (!times?.length) return "";

  return times
    .map((time) => {
      const start = datetimeDecorator.toHourMinutes(new Date(time.startAt));
      const end = datetimeDecorator.toHourMinutes(new Date(time.endAt));
      return `${start}-${end}`;
    })
    .join(", ");
};

const calculatePaymentAmountTotal = (
  paymentAmount: ShipmentOrderLegRunPaymentAmountEntity | null
): number => {
  if (!paymentAmount) return 0;

  return [
    paymentAmount.basicFeeYen || 0,
    paymentAmount.ancillaryFeeYen || 0,
    paymentAmount.expensesFeeYen || 0,
    paymentAmount.highwayFeeYen || 0,
    paymentAmount.loadingFeeYen || 0,
    paymentAmount.relayFeeYen || 0,
    paymentAmount.surchargeFeeYen || 0,
    paymentAmount.unloadingFeeYen || 0,
    paymentAmount.waitingTimeFeeYen || 0,
  ]
    .filter((fee) => fee)
    .reduce((acc, fee) => acc + Number(fee), 0);
};

const ensurePaymentAmount = (
  paymentAmount: ShipmentOrderLegRunPaymentAmountEntity | null
): ShipmentOrderLegRunPaymentAmountEntity => {
  if (paymentAmount) return paymentAmount;

  return {
    basicFeeYen: null,
    ancillaryFeeYen: null,
    expensesFeeYen: null,
    highwayFeeYen: null,
    loadingFeeYen: null,
    relayFeeYen: null,
    surchargeFeeYen: null,
    unloadingFeeYen: null,
    waitingTimeFeeYen: null,
    ancillaryContent: "",
  };
};

export const buildShipmentLegRunItems = (
  shipmentData: ShipmentOrderResponseEntity
): ShipmentLegRunItem[] => {
  const loadingWaypoint = shipmentData.shipmentOrderLoadingWaypoint;
  const unloadingWaypoint = shipmentData.shipmentOrderUnloadingWaypoint;

  const loadingTimes =
    loadingWaypoint.shipmentOrderWaypointLoadingTimes ?? [];
  const unloadingTimes =
    unloadingWaypoint.shipmentOrderWaypointUnloadingTimes ?? [];

  const loadingOn = getEarliestDateFormatted(loadingTimes);
  const unloadingOn = getEarliestDateFormatted(unloadingTimes);
  const loadingTimeWindow = formatTimeWindow(loadingTimes);
  const unloadingTimeWindow = formatTimeWindow(unloadingTimes);

  return shipmentData.shipmentOrderLegs.flatMap((leg) =>
    leg.shipmentOrderLegRuns.map((run) => {
      const quantity = run.items.reduce(
        (acc, item) => acc + (item.quantity || 0),
        0
      );
      const weightKg = run.items.reduce(
        (acc, item) => acc + (item.weightKg || 0),
        0
      );
      const volumeM3 = run.items.reduce(
        (acc, item) => acc + (item.volumeM3 || 0),
        0
      );
      const paymentAmount = ensurePaymentAmount(run.paymentAmount);

      return {
        id: run.id,
        shipmentId: shipmentData.id,
        sectionType: leg.stats,
        deliveryMethod: shipmentData.deliveryMethod,
        shipperName: shipmentData.shipperName,
        route: `${loadingWaypoint.name} → ${unloadingWaypoint.name}`,
        loadingAddress: loadingWaypoint.address,
        unloadingAddress: unloadingWaypoint.address,
        loadingOn,
        unloadingOn,
        loadingTimeWindow,
        unloadingTimeWindow,
        loadingStayingMinutes: loadingWaypoint.stayingMinutes as number,
        unloadingStayingMinutes: unloadingWaypoint.stayingMinutes as number,
        quantity,
        weightKg,
        volumeM3,
        paymentAmountTotal: calculatePaymentAmountTotal(paymentAmount),
        dispatchStatus: run.planningLicenseId ? "配車済" : "未配車",
        carrierCompany: run.carrierCompany,
        operatingCarrier: run.operatingCarrier,
        truckNumber: run.truckNumber,
        driverName: run.driverName,
        hierarchyLevel: run.hierarchyLevel,
        planningLicenseName: run.planningLicenseName,
        paymentAmount,
      } as ShipmentLegRunItem;
    })
  );
};
